(ns hai.sharing.people
  "Who an entity is shared with, as the rows its Share tab lists: the owner and
   each share, from the entity as saved or from the shares a draft stages.

   A row is `:key`, what tells it from the rest; `:person`, a user's uuid,
   name and email, or `:scope`, an audience; the `:permission` its role holds,
   or `owner`; `:me?` and `:assigned?`; and `:entry`, the share it stands for.")

(def owner ::owner)

(defn share-permission
  "The permission a share row shows. A share holds one permission; rows from
   before that rule hold several, and the highest wins."
  [permissions]
  (or (some (set permissions) [:permission.type/admin
                               :permission.type/update
                               :permission.type/read])
      (first permissions)))

(defn- person-row
  [person permission {:keys [current-user-uuid assignee-uuid]}]
  (let [user-uuid (:user/uuid person)]
    {:key (str user-uuid)
     :person person
     :permission permission
     :me? (and (some? user-uuid) (= user-uuid current-user-uuid))
     :assigned? (and (some? user-uuid) (= user-uuid assignee-uuid))}))

(defn- share-row
  [entry viewer]
  (let [permission (share-permission (:entity/permissions entry))]
    (assoc (if-let [scope (:entity/scope entry)]
             {:key scope :scope scope :permission permission}
             (person-row (select-keys entry [:user/uuid :user/full-name :user/email]) permission viewer))
           :entry entry)))

(defn entity-rows
  "`entity`'s owner and each of its shares. `viewer` holds the
   `:current-user-uuid`, whose row is theirs, and the `:assignee-uuid`."
  [{entity-owner :entity/owner shared-with :entity/shared-with} viewer]
  (cond->> (mapv #(share-row % viewer) shared-with)
    entity-owner (into [(person-row entity-owner owner viewer)])))

(defn staged-share
  "A share a draft stages: `recipient`, as `share/create` takes it in `share`,
   at `permission`."
  [recipient share permission]
  {:key (:key recipient)
   :recipient recipient
   :share share
   :permission permission})

(defn- recipient-person
  [{:keys [kind contact email]}]
  (if (= :contact kind)
    {:user/full-name (:contact/full-name contact)
     :user/email (:contact/email contact)}
    {:user/email [email]}))

(defn- staged-row
  [{:keys [recipient] :as staged}]
  (merge (select-keys staged [:key :permission])
         {:entry staged}
         (if (= :domain (:kind recipient))
           {:scope (:domain recipient)}
           {:person (recipient-person recipient)})))

(defn draft-rows
  "The rows of a draft `current-user` is making: theirs as its owner, and each
   of the shares it `staged`."
  [current-user staged]
  (cond->> (mapv staged-row staged)
    current-user (into [(person-row current-user owner {:current-user-uuid (:user/uuid current-user)})])))

(defn restage
  "`staged` with the share under `share-key` at `permission`."
  [staged share-key permission]
  (mapv #(cond-> %
           (= share-key (:key %)) (assoc :permission permission))
        staged))

(defn unstage
  "`staged` without the share under `share-key`."
  [staged share-key]
  (filterv #(not= share-key (:key %)) staged))
